import appsignal from "utils/appsignal";

/** Shared :playback_observability plumbing for the media players.
 *
 * The video and audio controllers report the same shape of thing to the same place, but have
 * almost nothing else in common (adaptive vs progressive, hls.js vs a bare <audio> element), so
 * what they share is exactly this: how to turn a value into a tag, how to talk to the AppSignal
 * client, and how to read an expiry out of a signed CDN URL.
 *
 * Every function here swallows its own failures. Instrumentation must never be the reason a
 * listener's episode stops playing.
 */

/** AppSignal tags and breadcrumb metadata are flat string maps. Passing null/undefined through
 *  produces the literal strings "null"/"undefined", and an empty string produces a tag whose
 *  presence means nothing - so drop all three rather than recording a lie. */
export function stringifyValues(object) {
  return Object.entries(object).reduce((accumulator, [key, value]) => {
    if (value === null || value === undefined || value === "") return accumulator;
    accumulator[key] = String(value);
    return accumulator;
  }, {});
}

/** Returns the send Promise, so a caller that is about to navigate away can wait on it, or
 *  undefined when the SDK is unavailable.
 *
 *  The callback form of sendError is deliberate: passing a plain tags object still works but logs
 *  a DEPRECATED warning on every report. */
export function sendPlaybackError(message, { action, tags = {} } = {}) {
  try {
    const merged = stringifyValues(tags);

    return appsignal.sendError(new Error(message), (span) => {
      if (action) span.setAction(action);
      span.setTags(merged);
    });
  } catch {
    // With no key configured the client runs in "development mode" and rethrows the original
    // error straight back out of sendError; the SDK may also not have loaded at all. Either way,
    // swallow.
    return undefined;
  }
}

/** Breadcrumbs accumulate on the shared AppSignal client and are attached to the next report it
 *  sends - which is the "how did we get here" trail we want on a failure. Note they are global to
 *  the client, not per caller, and are cleared once attached. */
export function sendPlaybackBreadcrumb(category, action, metadata = {}) {
  try {
    appsignal.addBreadcrumb({
      category: category,
      action: action,
      metadata: stringifyValues(metadata)
    });
  } catch {
    // Never let instrumentation break the playback it is observing.
  }
}

/** HTMLMediaElement's MediaError codes. Reported as names because the bare integers are
 *  meaningless in an AppSignal tag list, and because the distinction they draw is the one that
 *  decides who owns a failure: `network` is ours (CDN, token, connectivity), `decode` and
 *  `src_not_supported` are the asset's or the browser's, `aborted` is usually the listener. */
const MEDIA_ERROR_CATEGORIES = {
  1: "aborted", // MEDIA_ERR_ABORTED
  2: "network", // MEDIA_ERR_NETWORK
  3: "decode", // MEDIA_ERR_DECODE
  4: "src_not_supported" // MEDIA_ERR_SRC_NOT_SUPPORTED
};

export function mediaErrorCategory(code) {
  return MEDIA_ERROR_CATEGORIES[code] || "unknown";
}

/** When a signed CDN URL stops being playable, in epoch milliseconds, or null when the URL
 *  carries no readable expiry.
 *
 *  CloudFront's canned policy puts it in `Expires` and Bunny's token auth in `expires`, both in
 *  epoch seconds - the Ruby counterpart is Playback::SignedUrlExpiry, and the two must agree.
 *
 *  null is "unknown", not "expired": a Rails redirect route or the feeds-service embed URL is
 *  perfectly valid and simply doesn't advertise a deadline. Callers must not default it to now. */
export function signedUrlExpiryMs(url) {
  if (!url) return null;

  try {
    const parsed = new URL(url, window.location.origin);
    const raw = parsed.searchParams.get("Expires") || parsed.searchParams.get("expires");
    if (!raw) return null;

    const seconds = Number.parseInt(raw, 10);
    if (!Number.isFinite(seconds) || seconds <= 0) return null;

    return seconds * 1000;
  } catch {
    return null;
  }
}
